/** @jsxRuntime classic */
/** @jsx jsx */
import { useRef, useEffect, useState, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Typography as Text } from '@mui/material';
import Select from '../common/Select';

import UploadSettings from './UploadSettings';

import RecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import MicIcon from '@mui/icons-material/Mic';

import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';

import AudioWave from '../common/AudioWave';
import AudioDuration from '../common/AudioDuration';
import { confirmDialog } from '../../utils/dialog';

import { getStream } from '../../utils/media';
import { RecordingsDB } from '../../utils/db_utils';

const recordingsDB = new RecordingsDB();

export default function FileRecorder({
  selectedAudioDevice,
  file,
  onFile,
  settings,
  wakeLock,
  onAudioDeviceChange,
  audioDevices,
  exceedsUploadLimit,
  setSettings,
  onUpload,
  onReset,
}) {
  const { t } = useTranslation();
  const parentRef = useRef(null);
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const [recId, setRecId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    return () => {
      if (recorder) {
        recorder.stop();
      }
    };
  }, [recorder]);

  const startRecording = useCallback(async () => {
    if (selectedAudioDevice) {
      try {
        // make sure clean up any previous recordings
        // should not be necessary, but seems to be some
        // weird state where this is needed. I dont know why
        // but double checking that the object store is empty
        // should solve it.... Belts and suspenders so to speak
        await recordingsDB.clearObjectStore();

        const stream = await getStream(selectedAudioDevice);
        const allowedMimeTypes = [
          'audio/mp4',
          'audio/webm',
          'audio/ogg',
          'audio/wav',
        ];
        const mimeType = allowedMimeTypes.find((mimetype) =>
          MediaRecorder.isTypeSupported(mimetype)
        );
        const recorder = new MediaRecorder(stream, { mimeType });

        if (!wakeLock.current) {
          try {
            wakeLock.current = await navigator.wakeLock.request('screen');
          } catch (err) {
            console.error('Could not request wake lock', err);
          }
        }

        recorder.addEventListener('dataavailable', (e) => {
          recordingsDB.saveBlobChunkToIndexedDB(e.data);
        });

        recorder.addEventListener('stop', () => {
          stream.getTracks().forEach((track) => track.stop());
          setRecording(false);

          recordingsDB.getAllBlobsFromIndexedDB().then((blobs) => {
            recordingsDB.concatenateBlobs(blobs, mimeType).then((finalBlob) => {
              onFile(finalBlob);
            });
          });
        });

        recorder.addEventListener('pause', () => {
          setIsPaused(true);
        });

        recorder.addEventListener('resume', () => {
          setIsPaused(false);
        });

        recordingsDB.saveSettingsToIndexedDB(settings);
        recorder.start(1000);
        setRecording(true);
        setRecorder(recorder);
      } catch (err) {
        console.error('Error starting recording', err);
        setError(err);
      }
    }
  }, [file, selectedAudioDevice, recorder]);

  const stopRecording = useCallback(() => {
    if (recorder) {
      recorder.stop();
      setRecording(false);
      setRecorder(null);
      if (wakeLock.current) {
        try {
          wakeLock.current.release();
        } catch (err) {
          console.error('Could not release wake lock', err);
        }
      }
    }
  }, [recorder, wakeLock]);

  const handleRecorderClick = useCallback(() => {
    if (!recording && !recorder) {
      startRecording();
      setRecId(Date.now());
    } else if (recording) {
      if (recorder.state === 'recording') {
        recorder.pause();
        setIsPaused(true);
      } else if (recorder.state === 'paused') {
        recorder.resume();
        setIsPaused(false);
      }
    }
  }, [recording, startRecording, stopRecording, recorder]);

  const selectedDeviceLabel = audioDevices.find(
    (device) => device.deviceId === selectedAudioDevice
  )?.label;

  const handleReset = useCallback(async () => {
    const ok = await confirmDialog({
      title: t('Ta bort inspelningen?'),
      content: t('Är du säker på att du vill ta bort inspelningen?'),
    });

    if (ok) {
      recorder?.stop();
      try {
        await recordingsDB.clearObjectStore();
      } catch (err) {
        console.error(err);
        setError(err);
      }
      onFile(null);
      setIsPaused(false);
      setRecording(false);
      onReset();
      setRecorder(null);
      setRecId(null);
    }
  }, [setIsPaused, setRecording, recorder]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        gap: 1rem;
      `}
      ref={parentRef}
    >
      {error ? (
        <Text variant="body2" color="error">
          {error.message}
        </Text>
      ) : null}
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          align-items: center;
          gap: 1rem;
        `}
      >
        <Button
          endIcon={<DeleteOutlinedIcon />}
          disabled={(!isPaused && recording) || !recorder}
          variant={
            (!isPaused && recording) || !recorder ? 'contained' : 'outlined'
          }
          color={'text'}
          sx={{
            borderRadius: '2rem',
            maxHeight: 40,
          }}
          onClick={() => {
            handleReset();
          }}
          css={css`
            grid-column: 1;
          `}
        >
          {t('Släng')}
        </Button>
        <Button
          endIcon={<RecordIcon />}
          onClick={handleRecorderClick}
          variant={recording || !!recorder ? 'outlined' : 'contained'}
          color="error"
          sx={{
            borderRadius: '2rem',
            height: 48,
          }}
          css={css`
            grid-column: 2;
          `}
          disabled={exceedsUploadLimit || audioDevices.length === 0}
        >
          {!isPaused && recording
            ? t('Pausa')
            : !!recorder
            ? t('Återuppta')
            : t('Spela in')}
        </Button>
      </div>
      <div
        css={css`
          background-color: red;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          background-color: var(--primary-8p);
          border-radius: 0.5rem;
          padding: 0.5rem;
          padding-top: 1rem;
        `}
      >
        <AudioWave recorder={recorder} />
        <AudioDuration isRecording={recording && !isPaused} recId={recId} />
        <div
          css={css`
            width: 100%;
          `}
        >
          <Collapse in={!recording}>
            <div
              css={css`
                width: 100%;
                margin-bottom: 1rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                margin-top: 1rem;
              `}
            >
              <div
                css={css`
                  display: flex;
                  gap: 0.5rem;
                  align-items: center;
                `}
              >
                <MicIcon
                  css={css`
                    color: white;
                    background-color: var(--primary-600);
                    border-radius: 50%;
                    padding: 4px;
                  `}
                  fontSize="small"
                />
                <Text
                  variant="subtitle2"
                  fontWeight="600"
                  css={css`
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {t('Mikrofon ({{mic}})', { mic: selectedDeviceLabel })}
                </Text>
              </div>
              <Select
                size="small"
                value={selectedAudioDevice}
                onChange={(e) => {
                  window.localStorage.setItem(
                    'preferredAudioDevice',
                    e.target.value
                  );
                  onAudioDeviceChange(e.target.value);
                }}
                options={audioDevices.map((device) => ({
                  value: device.deviceId,
                  label: device.label,
                }))}
                disabled={audioDevices.length === 1 || exceedsUploadLimit}
                fullWidth
              />
            </div>
          </Collapse>
        </div>
      </div>
      <div
        css={css`
          width: 100%;
        `}
      >
        <Collapse in={isPaused}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={settings?.folderPrefix?.split('/').length <= 2}
            onClick={onUpload}
          >
            {t('Ladda upp')}
          </Button>
          <UploadSettings
            settings={settings}
            setSettings={setSettings}
            disabled={exceedsUploadLimit}
            nameEdit
          />
        </Collapse>
      </div>
    </div>
  );
}
